import { Middleware } from 'redux';
import WebSocket from 'isomorphic-ws';
import { actions, IStore } from './store';
import { QuoteResponseWrapper } from './model/quoteResponseWrapper';
import { ConnectionStatus } from './constants';

export type MarketDataWsMiddlewareConfig = {
  debug?: boolean;
  debugLog?: typeof console.log;
  saveMessagesAndErrors?: boolean;
};

export const marketDataWsMiddleware: (config?: MarketDataWsMiddlewareConfig) => Middleware<{}, IStore> =
  ({ debug = false, debugLog = console.log, saveMessagesAndErrors = false } = {}) =>
  // eslint-disable-next-line sonarjs/cognitive-complexity
  (store) => {
    let socket: WebSocket | undefined;
    const onOpen = (ev: WebSocket.Event): void => {
      const { type } = ev;
      if (debug) debugLog('onopen', { type });
      store.dispatch(actions.connectionStatusChanged(ConnectionStatus.Connected));
    };
    const onClose = (ev: WebSocket.CloseEvent): void => {
      const { code, reason, type, wasClean } = ev;
      if (debug) debugLog('onclose', { code, reason, type, wasClean });
      store.dispatch(actions.connectionStatusChanged(ConnectionStatus.Disconnected));
    };
    const onMessage = (msg: WebSocket.MessageEvent): void => {
      if (debug) debugLog('onmessage', msg);
      const message = JSON.parse(msg.data.valueOf() as string);
      if ((message as QuoteResponseWrapper).quoteV1) store.dispatch(actions.quoteV1MessageReceived(message as QuoteResponseWrapper));
      else store.dispatch(actions.unknownMessageReceived(message));
    };
    const onError = (err: WebSocket.ErrorEvent): void => {
      const { error, message, type } = err;
      if (debug) debugLog('onerror', { error, message, type });
      store.dispatch(actions.errorReceived(message));
    };

    return (next) => (action) => {
      // eslint-disable-next-line unicorn/prefer-regexp-test
      if (actions.connect.match(action)) {
        store.dispatch(actions.connectionStatusChanged(ConnectionStatus.Connecting));
        store.dispatch(actions.setSaveMessagesAndErrors(saveMessagesAndErrors));
        if (debug) debugLog({ ...action.payload });
        socket = new WebSocket(action.payload.wsUrl, action.payload.origin ? { origin: action.payload.origin } : undefined);
        socket.addEventListener(`open`, onOpen);
        socket.addEventListener(`close`, onClose);
        socket.addEventListener(`error`, onError);
        socket.addEventListener(`message`, onMessage);
        // eslint-disable-next-line unicorn/prefer-regexp-test
      } else if (actions.disconnect.match(action)) {
        store.dispatch(actions.connectionStatusChanged(ConnectionStatus.Disconnecting));
        if (debug) debugLog('close');
        socket?.close();
        socket = undefined;
        store.dispatch(actions.connectionStatusChanged(ConnectionStatus.Disconnected));
        // eslint-disable-next-line unicorn/prefer-regexp-test
      } else if (actions.send.match(action)) {
        if (debug) debugLog('send', action.payload);
        const payload = JSON.stringify(action.payload);
        store.dispatch(actions.messageSent(payload));
        socket?.send(payload, (err) => {
          if (err) {
            if (debug) debugLog('sent - error', err);
            store.dispatch(actions.errorReceived(err.message));
          } else {
            if (debug) debugLog('sent - no error');
          }
        });
      }

      next(action);
    };
  };
